import ResponseHelper from './response-helper';

/**
 * Lib that manages communication with Vizzto server related to app's navigation.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

function getMenu(axios, utoken) {
  return axios({
    method: 'get',
    url: 'menu?type=BROWSER',
    headers: {
      utoken
    }
  }).then(function (response) {
    const d = response.data;
    return {
      showMenuContent: d.showMenuContent,
      showMenuInformation: d.showMenuInformation,
      menuContents: d.menuContents,
      menuInformations: d.menuInformations
    };
  });
}

function getChannels(axios, utoken, distributorId) {
  return axios({
    method: 'get',
    url: `channels?&distributorId=${distributorId}`,
    headers: {
      utoken
    }
  }).then(function (response) {
    return {
      data: response.data
    };
  });
}

function getCategories(axios, utoken) {
  return axios({
    method: 'get',
    url: 'categories/order/content',
    headers: {
      utoken
    }
  }).then(function (response) {
    return {
      data: response.data
    };
  });
}

function getContent(axios, distributorId, utoken, numberType) {
  return axios({
    method: 'get',
    url: `distributor/${distributorId}/genre/type/${numberType}/content`,
    headers: {
      utoken
    }
  }).then(function (response) {
    return {
      data: response.data
    };
  });
}

function switchContent(axios, distributorId, utoken, content, contentCode) {
  switch (content) {
    case 'VIDEOS':
    case 'MOVIES':
    case 'SERIES':
    case 'COURSES':
    case 'VIDEO_LESSONS':
    case 'LECTURE':
      return getContent(axios, distributorId, utoken, contentCode);
    case 'CHANNELS':
      return getChannels(axios, utoken, distributorId);
    case 'CATEGORIES':
      return getCategories(axios, utoken);
    default:
      return { data: [] };
  }
}

export default (axios) => ({
  getContentTypeCode(contentTypeName) {
    const typeMap = {
      VIDEOS: 0,
      MOVIES: 1,
      SERIES: 3,
      COURSES: 16,
      VIDEO_LESSONS: 21,
      LECTURE: 22
    };
    return typeMap[contentTypeName] || 0;
  },

  /**
   * Gets links for navigation through the app. Usually those links are in header and footer.
   *
   * Obs:
   * Hoje é retornado uma lista de ENUMS contendo todos os itens que devem compor o menu lateral.
   * Não é previsto no BD os conteudos do footer
   * {“showMenuContent”:true,“showMenuInformation”:true,“menuContents”:[“CHANNELS”,“MOVIES”,“SERIES”,“COURSES”,“VIDEOS”,“MY_LIST”,“VIDEO_LESSONS”,“LECTURES”,“LOGOUT”],“menuInformations”:[“MY_ACCOUNT”,“PURCHASE_HISTORY”]}
   *
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @returns {Promise<{code: Number, message: String, data: Object}>}
   */
  getAppLinks(distributorId, userToken) {
    const menus = [
      {
        prop: 'menuContents',
        show: { prop: 'showMenuContent', value: true },
        items: []
      },
      {
        prop: 'menuInformations',
        show: { prop: 'showMenuInformation', value: true },
        items: []
      }
    ];
    return getMenu(axios, userToken).then((menuData) => {
      let promiseChain = Promise.resolve();
      for (let a = 0, n = menus.length; a < n; a++) {
        const menu = menus[a];
        const types = menuData.hasOwnProperty(menu.prop)
          ? menuData[menu.prop]
          : [];
        menu.show.value = menuData[menu.show.prop];
        for (let b = 0, m = types.length || 0; b < m; b++) {
          const menuType = types[b];
          const menuObj = {
            type: menuType,
            items: []
          };
          menu.items.push(menuObj);
          promiseChain = promiseChain
            .then(() => {
              return switchContent(
                axios,
                distributorId,
                userToken,
                menuType,
                this.getContentTypeCode(menuType)
              );
            })
            .then((response) => {
              menuObj.items = response.data;
              return response;
            });
        }
      }
      promiseChain = promiseChain
        .then(() => {
          return {
            data: {
              menuInformation: menus[1].items,
              menuContent: menus[0].items,
              showMenuContent: menus[0].show.value,
              showMenuInformation: menus[1].show.value
            },
            code: 0, // Code = 0 means user is logged in
            message: 'OK'
          };
        })
        .catch((error) => {
          return Promise.reject(ResponseHelper.getErrorResponse(error));
        });
      return promiseChain;
    });
  }
});
